import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AdminListCard,
  AdminListTable,
  ConfirmDeleteModal,
  PageTabs,
  saveErrorReason,
  useBatchSelection,
  withFailureReasons,
} from "@components/CatalogIndex";
import type { BatchFailure } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { snakeToText } from "@utils/helperFunctions";
import useFlags from "@common/hooks/useFlags";
import { useDocumentTemplatesList, useDocumentTemplateMutations } from "../api";
import { buildDocumentTemplateBatchActions } from "./batchActions";
import type { DocumentTemplate, DocumentTemplateTab } from "../types";

const PAGE_SIZE = 25;

interface ListProps {
  /** Access level for `settings.sow_templates`; "view" is read-only. */
  permission?: string;
}

const List = ({ permission }: ListProps): JSX.Element => {
  const navigate = useNavigate();
  const canEdit = permission !== "view";
  const { releaseChangeOrders } = useFlags();

  const [tab, setTab] = useState<DocumentTemplateTab>("active");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState("");
  const [nameFilter, setNameFilter] = useState("");

  const sel = useBatchSelection([tab]);
  const { archive, restore } = useDocumentTemplateMutations();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const { rows, pageMeta, isLoading, isFetching, refetch } =
    useDocumentTemplatesList({
      page: { number: pageNumber, size: pageSize },
      filter: {
        active: tab === "active" ? [true] : [false],
        ...(nameFilter ? { name: nameFilter } : {}),
      },
    });

  const selectedTemplates = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(String(r.id))),
    [rows, sel.selectedIds]
  );

  const columns: ColumnDef<DocumentTemplate>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <>
          {row.original.name}
          {releaseChangeOrders && row.original.changeOrderDefault && (
            <span className="badge badge-info ml-2">Change orders</span>
          )}
        </>
      ),
    },
    {
      accessorKey: "format",
      header: "Template Format",
      cell: ({ row }) =>
        row.original.format ? snakeToText(row.original.format) : "",
    },
    {
      accessorKey: "templateFormat",
      header: "Data Format",
      cell: ({ row }) =>
        row.original.templateFormat
          ? snakeToText(row.original.templateFormat)
          : "",
    },
    {
      accessorKey: "includeFormatting",
      header: "Formatting Enabled?",
      cell: ({ row }) => (row.original.includeFormatting ? "Yes" : "No"),
    },
  ];

  const runOnSelected = async (
    fn: (t: DocumentTemplate) => Promise<unknown>,
    successText: (n: number) => string
  ) => {
    const targets = [...selectedTemplates];
    const failures: BatchFailure[] = [];
    for (const t of targets) {
      try {
        await fn(t);
      } catch (err) {
        failures.push({ label: t.name, reason: saveErrorReason(err) });
      }
    }
    setToast(
      failures.length === 0
        ? { variant: "success", text: successText(targets.length) }
        : {
            variant: "danger",
            text: withFailureReasons(
              `Failed on ${failures.length} of ${targets.length} document template(s).`,
              failures
            ),
          }
    );
    sel.clear();
    refetch();
  };

  const handleConfirmDeleteOrRestore = async () => {
    setConfirmOpen(false);
    if (tab === "active") {
      await runOnSelected(
        (t) => archive(String(t.id)),
        (n) => `${n} document template(s) archived.`
      );
    } else {
      await runOnSelected(
        (t) => restore(String(t.id)),
        (n) => `${n} document template(s) restored.`
      );
    }
  };

  const batchActions = canEdit
    ? buildDocumentTemplateBatchActions(tab, sel.selectedIds.length, {
        onDelete: () => setConfirmOpen(true),
        onRestore: () => setConfirmOpen(true),
      })
    : [];

  const submitSearch = () => {
    setPageNumber(1);
    setNameFilter(searchInput);
  };

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminListCard
        title="Document Templates"
        onNew={
          canEdit ? () => navigate("/admin/document_templates/new") : undefined
        }
        newButtonLabel="+ Add Document Template"
        searchProps={{
          label: "Search Document Templates",
          placeholder: "Search by name",
          value: searchInput,
          onChange: (e) => setSearchInput(e.target.value),
          onSubmit: (e) => {
            e.preventDefault();
            submitSearch();
          },
          onClear: () => {
            setSearchInput("");
            setPageNumber(1);
            setNameFilter("");
          },
        }}
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <PageTabs
          activeKey={tab}
          onSelect={(key) => {
            setTab(key as DocumentTemplateTab);
            setPageNumber(1);
            sel.clear();
          }}
          tabs={[
            {
              key: "active",
              label: "Active",
              count: tab === "active" ? pageMeta.recordCount : undefined,
            },
            {
              key: "archived",
              label: "Archived",
              count: tab === "archived" ? pageMeta.recordCount : undefined,
            },
          ]}
        />
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onEdit={(id) => navigate(`/admin/document_templates/edit/${id}`)}
          editLinkDisabled={!canEdit}
          emptyMessage="No document templates found."
          paginationEnabled
          pageSizeEnabled
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={(p) => {
            sel.clear();
            setPageNumber(p);
          }}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            sel.clear();
            setPageSize(size);
            setPageNumber(1);
          }}
        />
      </AdminListCard>

      <ConfirmDeleteModal
        open={confirmOpen}
        title={
          tab === "active"
            ? "Archive document templates?"
            : "Restore document templates?"
        }
        message={`Are you sure you want to ${
          tab === "active" ? "archive" : "restore"
        } ${sel.selectedIds.length} document template(s)?`}
        confirmLabel={tab === "active" ? "Archive" : "Restore"}
        onConfirm={handleConfirmDeleteOrRestore}
        onCancel={() => setConfirmOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default List;
